import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Droplets } from "lucide-react";
import { SITE_ORIGIN } from "@/components/nyla/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardPreview = (p) => {
  if (p.excerpt?.trim()) return p.excerpt.trim();
  const text = (p.content_html || (p.content || []).join(" "))
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 220 ? `${text.slice(0, 220).replace(/\s+\S*$/, "")}…` : text;
};

export const Blog = ({ limit }) => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data))
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  const shown = limit ? posts.slice(0, limit) : posts;
  const Heading = limit ? "h2" : "h1";

  // Blog + BlogPosting list schema — emitted only on the full Journal listing
  // so every published post (old and future) is structured automatically.
  const blogJsonLd = !limit && {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE_ORIGIN}/journal#blog`,
    name: "The Nyla Journal",
    url: `${SITE_ORIGIN}/journal`,
    description: "Stories of water, health, and the planet — from the makers of Nyla Air Water.",
    inLanguage: "en-IN",
    publisher: { "@type": "Organization", "@id": `${SITE_ORIGIN}/#organization`, name: "Nyla Air Water" },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      "@id": `${SITE_ORIGIN}/blog/${p.slug}#article`,
      headline: p.title,
      description: p.excerpt,
      url: `${SITE_ORIGIN}/blog/${p.slug}`,
      image: p.image ? [p.image] : undefined,
      datePublished: p.published_at,
      author: { "@type": "Person", name: p.author || "Nyla Editorial" },
      articleSection: p.category,
    })),
  };

  return (
    <section id="journal" data-testid="blog-section" className="py-24 md:py-32 lg:py-40 bg-white">
      {blogJsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(blogJsonLd)}</script>
        </Helmet>
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24 max-w-3xl mx-auto"
        >
          <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">The Nyla Journal</p>
          <Heading data-testid="blog-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]">
            Stories of water, health, and the planet.
          </Heading>
          <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
            Notes from our journey — the science of purity, the craft behind every bottle,
            and the future of sustainable hydration.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {shown.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
            >
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block bg-[#F5F5F7] rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 ease-out h-full"
              >
                <div className="h-56 overflow-hidden">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                    />
                  ) : (
                    <div
                      data-testid={`blog-card-placeholder-${p.slug}`}
                      className="w-full h-full bg-gradient-to-br from-[#2C3E35] to-[#131F19] flex flex-col items-center justify-center gap-3"
                    >
                      <Droplets className="w-9 h-9 text-white/25" strokeWidth={1.4} />
                      <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-white/30">
                        The Nyla Journal
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#2C3E35]">{p.category}</span>
                    <span className="text-xs text-[#86868B]">{p.read_time}</span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-bold text-[#1D1D1F] leading-snug mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#86868B] mb-5 line-clamp-4">{cardPreview(p)}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2C3E35]">
                    Read article
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {limit && posts.length > limit && (
          <div className="text-center mt-14">
            <Link
              to="/journal"
              data-testid="blog-view-all-link"
              className="inline-flex items-center gap-1.5 px-7 py-3.5 rounded-full border border-[#D2D2D7] text-sm font-medium text-[#1D1D1F] hover:bg-[#F5F5F7] active:scale-95 transition-[background-color,transform] duration-200"
            >
              View all articles
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
